import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { itilQuery } from '../api/overview'
import { shellQuery, moduleOf } from '../api/shell'
import { Content } from '../ui/Shell'
import { usePageStyles } from '../ui/usePageStyles'
import { formatNumber, phpDayTime, sparkline } from './format'
import { hrefOf } from './href'

/**
 * Overview::dashboard - tabloul ITIL, macheta dashboard/index.php.
 * GET /api/v2/overview/itil?days=. SVG sparkline, nu AmCharts.
 * Legaturile Easeedesk raman in portal; cele moarte Overview nu se deseneaza.
 */

const STYLES = ['css/adm.css', 'css/overview_itil.css']
const DAYS = [7, 30, 90] as const
export type ItilDays = (typeof DAYS)[number]

function num(v: unknown): number {
  return Number(v) || 0
}

function tile(opts: { value: string; label: string; note?: string; tone: string; link?: string | null }) {
  const inner = (
    <div className={`itil-tile itil-tile-${opts.tone}`}>
      <div className="itil-tile-value" dangerouslySetInnerHTML={{ __html: opts.value }} />
      <div className="itil-tile-label">{opts.label}</div>
      {opts.note ? <div className="itil-tile-note">{opts.note}</div> : null}
    </div>
  )
  const href = opts.link ? hrefOf(opts.link) : null
  if (!href) return inner
  return (
    <a className="itil-tile-link" href={href}>
      {inner}
    </a>
  )
}

function Section({
  id,
  tone,
  icon,
  title,
  note,
  link,
  linkLabel,
}: {
  id: string
  tone: string
  icon: string
  title: string
  note: string
  link: string
  linkLabel: string
}) {
  const href = hrefOf(link)
  return (
    <div className={`adm-section adm-section-${tone}`} id={id}>
      <h4>
        <i className={`fas ${icon}`}></i> {title}
      </h4>
      <span className="adm-section-note">{note}</span>
      {href ? (
        <a className="adm-section-link" href={href}>
          {linkLabel} <i className="fa fa-arrow-circle-right"></i>
        </a>
      ) : null}
    </div>
  )
}

function Mini({
  rows,
  empty,
  wide,
  title,
  foot,
}: {
  rows: Array<{ label: string; href?: string | null; total: string; extra?: string; extraCls?: string }>
  empty: string
  wide?: boolean
  title: string
  foot?: string
}) {
  return (
    <div className={`itil-detail-col${wide ? ' itil-detail-wide' : ''}`}>
      <h5>{title}</h5>
      {rows.length === 0 ? (
        <p className="adm-muted itil-small" dangerouslySetInnerHTML={{ __html: empty }} />
      ) : (
        <>
          <table className="itil-mini">
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>
                    {row.href ? <a href={row.href}>{row.label}</a> : row.label}
                  </td>
                  <td className="itil-num">{row.total}</td>
                  {row.extra !== undefined ? <td className={`itil-num ${row.extraCls ?? ''}`}>{row.extra}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
          {foot ? <span className="adm-muted itil-small" dangerouslySetInnerHTML={{ __html: foot }} /> : null}
        </>
      )}
    </div>
  )
}

function readDays(): ItilDays {
  if (typeof window === 'undefined') return 30
  const n = Number(new URLSearchParams(window.location.search).get('days'))
  return DAYS.includes(n as ItilDays) ? (n as ItilDays) : 30
}

/**
 * Randul de provenienta de deasupra cardului: DUPA CE anume s-a numarat.
 *
 * `$mapNote()` din `dashboard/index.php`, si lipsea cu totul din rescriere.
 * Conteaza mai mult decat pare: `incidents.open` numara ORICE tichet deschis,
 * fara filtru pe proces, iar sub titlul "Incident Management" cifra aia se
 * citeste ca "atatea incidente". Cand niciun nume de categorie nu tine de
 * proces, randul o spune rosu si spune si ce se face in Settings ca sa se
 * indrepte. Un tablou care nu-si arata provenienta cifrelor e un tablou in
 * care nu poti avea incredere.
 */
export function MapNote({ entry, what }: { entry: { labels?: string[]; source?: string } | undefined; what: string }) {
  const labels = [...new Set(entry?.labels ?? [])].slice(0, 6)
  if (labels.length === 0) {
    return (
      <span className="itil-map itil-map-none">
        <i className="fas fa-exclamation-triangle"></i> <strong>{what}:</strong> no category, subcategory or ticket type
        carries the name of this process, so the figures below count <strong>every</strong> ticket. One category name in
        Settings fixes it.
      </span>
    )
  }
  return (
    <span className="itil-map">
      <i className="fas fa-filter"></i> <strong>{what}:</strong> {labels.join(' · ')}{' '}
      <em>({entry?.source === 'column' ? 'declared' : 'matched by name'})</em>
    </span>
  )
}

export function ItilPage() {
  const [days, setDays] = useState<ItilDays>(readDays)
  const shell = useQuery(shellQuery(moduleOf('/overview/dashboard')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const q = useQuery(itilQuery(days))
  const data = q.data ?? {}
  const changes = (data.changes ?? {}) as Record<string, unknown>
  const incidents = (data.incidents ?? {}) as Record<string, unknown>
  const problems = (data.problems ?? {}) as Record<string, unknown>
  const requests = (data.requests ?? {}) as Record<string, unknown>
  const rfc = (requests.rfc ?? {}) as Record<string, unknown>
  /* Cheile sunt codurile Itil_processes: 1 incident, 3 schimbare. */
  const map = (data.map ?? {}) as Record<string, { labels?: string[]; source?: string }>

  const byType = (Array.isArray(changes.by_type) ? changes.by_type : []) as Array<Record<string, unknown>>
  const bySource = (Array.isArray(changes.by_source) ? changes.by_source : []) as Array<Record<string, unknown>>
  const topCis = (Array.isArray(changes.top_cis) ? changes.top_cis : []) as Array<Record<string, unknown>>
  const trend = (Array.isArray(changes.trend) ? changes.trend : []) as Array<{ total?: number }>
  const incTrend = (Array.isArray(incidents.trend) ? incidents.trend : []) as Array<{ total?: number }>
  const byPriority = (Array.isArray(incidents.by_priority) ? incidents.by_priority : []) as Array<Record<string, unknown>>
  const incCis = (Array.isArray(incidents.top_cis) ? incidents.top_cis : []) as Array<Record<string, unknown>>
  const problemTop = (Array.isArray(problems.top) ? problems.top : []) as Array<Record<string, unknown>>
  const candidates = (Array.isArray(problems.candidates) ? problems.candidates : []) as Array<Record<string, unknown>>
  const byStatus = (Array.isArray(problems.by_status) ? problems.by_status : []) as Array<Record<string, unknown>>
  const next = (Array.isArray(requests.next) ? requests.next : []) as Array<Record<string, unknown>>

  return (
    <Content>
      <div className="adm-page-head adm-tone-blue-mauve">
        <span className="adm-page-icon">
          <i className="fas fa-project-diagram"></i>
        </span>
        <div className="adm-page-title">
          <div className="adm-page-title-line">
            <h3>ITIL Board</h3>
            <span className="adm-page-title-extra">
              <select
                className="form-control input-sm"
                id="itilDays"
                title="The window the figures are counted over"
                value={days}
                onChange={(e) => {
                  const nextDays = Number(e.target.value) as ItilDays
                  setDays(nextDays)
                  const url = new URL(window.location.href)
                  url.searchParams.set('days', String(nextDays))
                  window.history.replaceState(null, '', url.pathname + url.search)
                }}
              >
                <option value={7}>last 7 days</option>
                <option value={30}>last 30 days</option>
                <option value={90}>last 90 days</option>
              </select>
            </span>
          </div>
          <span className="adm-page-note">what changed, what is broken, what keeps breaking, and what is due to change</span>
        </div>
        <div className="adm-page-stats">
          <div className="adm-stat adm-stat-red-intense">
            <span className="adm-stat-value">{formatNumber(num(changes.unauthorized))}</span>
            <span className="adm-stat-label">unauthorised changes</span>
          </div>
          <div className="adm-stat adm-stat-yellow">
            <span className="adm-stat-value">{formatNumber(num(incidents.open))}</span>
            <span className="adm-stat-label">open incidents</span>
          </div>
          <div className="adm-stat adm-stat-blue-mauve">
            <span className="adm-stat-value">{formatNumber(num(problems.open))}</span>
            <span className="adm-stat-label">open problems</span>
          </div>
          <div className="adm-stat adm-stat-green-mode">
            <span className="adm-stat-value">{formatNumber(num(requests.upcoming))}</span>
            <span className="adm-stat-label">windows in 7 days</span>
          </div>
        </div>
      </div>

      <Section
        id="change-management"
        tone="red-intense"
        icon="fa-exchange-alt"
        title="Change Management"
        note="what changed on items - and what was not allowed to"
        link="adm/changes"
        linkLabel="All changes"
      />
      <div className="adm-card itil-card">
        {!changes.ready ? (
          <div className="adm-empty-inline">
            <i className="fas fa-database"></i>
            The change register is not in the database yet - see docs/tablou-itil.md.
          </div>
        ) : (
          <>
            <div className="itil-tiles">
              {tile({
                value: formatNumber(num(changes.unauthorized)),
                label: 'unauthorised',
                note: 'changes with no window and no ticket',
                tone: num(changes.unauthorized) > 0 ? 'red' : 'green',
                link: 'adm/changes',
              })}
              {tile({
                value: formatNumber(num(changes.total)),
                label: 'changes, total',
                note: 'agent, scan and operator all together',
                tone: 'grey',
              })}
              {tile({
                value: formatNumber(num(changes.by_window)),
                label: 'inside a window',
                note: 'planned ahead',
                tone: 'green',
              })}
              {tile({
                value: formatNumber(num(changes.by_ticket)),
                label: 'covered by a ticket',
                note: 'authorised after the fact',
                tone: 'yellow',
              })}
              {tile({
                value: changes.control_rate === null || changes.control_rate === undefined ? '&mdash;' : `${changes.control_rate}%`,
                label: 'control rate',
                note: 'how much of what changed was actually asked for',
                tone: changes.control_rate !== null && Number(changes.control_rate) >= 80 ? 'green' : 'yellow',
              })}
            </div>
            <div className="itil-detail">
              <div className="itil-detail-col">
                <h5>Unauthorised per day</h5>
                <span dangerouslySetInnerHTML={{ __html: sparkline(trend, 'red') }} />
                <span className="adm-muted itil-small">last {days} days</span>
              </div>
              <Mini
                title="Pe feluri"
                empty="nothing in this window"
                foot="total &middot; of which unauthorised"
                rows={byType.map((row) => ({
                  label: String(row.label ?? ''),
                  total: formatNumber(num(row.total)),
                  extra: formatNumber(num(row.unauthorized)),
                  extraCls: 'itil-bad',
                }))}
              />
              <Mini
                title="Pe surse"
                empty="nothing in this window"
                rows={bySource.map((row) => ({
                  label: String(row.label ?? ''),
                  total: formatNumber(num(row.total)),
                  extra: formatNumber(num(row.unauthorized)),
                  extraCls: 'itil-bad',
                }))}
              />
              <Mini
                title="Where things change without anyone asking"
                empty='<i class="fa fa-check-circle"></i> no item with unauthorised changes'
                wide
                rows={topCis.map((row) => ({
                  label: String(row.ci_name ?? row.name ?? ''),
                  href: hrefOf(`cmdb/view/${row.ci_id ?? row.ID ?? ''}`),
                  total: formatNumber(num(row.total)),
                }))}
              />
            </div>
          </>
        )}
      </div>

      <Section
        id="incident-management"
        tone="yellow"
        icon="fa-fire"
        title="Incident Management"
        note="what is broken right now, and on which CMDB item"
        link="easeedesk/tickets"
        linkLabel="Tichetele"
      />
      <div className="adm-card itil-card">
        {!incidents.ready ? (
          <div className="adm-empty-inline">
            <i className="fas fa-database"></i> The tickets table is not available.
          </div>
        ) : (
          <>
            <MapNote entry={map['1']} what="Incidents" />
            <div className="itil-tiles">
              {tile({
                value: formatNumber(num(incidents.open)),
                label: 'open',
                note: 'unresolved right now',
                tone: 'yellow',
                link: 'easeedesk/tickets',
              })}
              {tile({
                value: formatNumber(num(incidents.breached)),
                label: 'past SLA',
                note: 'of those open',
                tone: num(incidents.breached) > 0 ? 'red' : 'green',
              })}
              {tile({
                value: formatNumber(num(incidents.escalated)),
                label: 'escalated',
                note: 'past first line',
                tone: 'red',
              })}
              {tile({
                value: formatNumber(num(incidents.created)),
                label: 'incoming',
                note: 'in the chosen window',
                tone: 'grey',
              })}
              {tile({
                value: formatNumber(num(incidents.resolved)),
                label: 'resolved',
                note: 'in the chosen window',
                tone: 'green',
              })}
              {tile({
                value: incidents.mttr_hours === null || incidents.mttr_hours === undefined ? '&mdash;' : `${incidents.mttr_hours}h`,
                label: 'timp mediu de rezolvare',
                note: 'de la deschidere la rezolvat',
                tone: 'blue-hoki',
              })}
            </div>
            <div className="itil-detail">
              <div className="itil-detail-col">
                <h5>Incoming per day</h5>
                <span dangerouslySetInnerHTML={{ __html: sparkline(incTrend, 'yellow') }} />
              </div>
              <Mini
                title="By priority"
                empty="no open tickets"
                rows={byPriority.map((row) => ({
                  label: String(row.label ?? ''),
                  total: formatNumber(num(row.total)),
                }))}
              />
              <div className="itil-detail-col itil-detail-wide">
                <h5>Which items the incidents land on</h5>
                <p className="itil-small">
                  <strong>{formatNumber(num(incidents.on_cis))}</strong> of <strong>{formatNumber(num(incidents.created))}</strong> incoming
                  tickets touch an item
                  {num(incidents.created) > 0 ? (
                    <span className="adm-muted"> ({Math.round((num(incidents.on_cis) * 100) / num(incidents.created))}%)</span>
                  ) : null}
                </p>
                {incCis.length === 0 ? (
                  <p className="adm-muted itil-small">no incident linked to an item in this window</p>
                ) : (
                  <table className="itil-mini">
                    <tbody>
                      {incCis.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <a href={hrefOf(`cmdb/view/${row.ID ?? row.ci_id}`) ?? '#'}>{String(row.name ?? row.ci_name ?? '')}</a>
                          </td>
                          <td className="itil-num">{formatNumber(num(row.total))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Section
        id="problem-management"
        tone="blue-mauve"
        icon="fa-bug"
        title="Problem Management"
        note="what keeps breaking, and on what exactly"
        link="problems"
        linkLabel="Problems"
      />
      <div className="adm-card itil-card">
        {!problems.ready ? (
          <div className="adm-empty-inline">
            <i className="fas fa-database"></i>
            The problem tables are not in the database yet.
          </div>
        ) : (
          <>
            <div className="itil-tiles">
              {tile({
                value: formatNumber(num(problems.open)),
                label: 'open problems',
                note: 'under investigation or a known error',
                tone: 'blue-mauve',
                link: 'problems',
              })}
              {byStatus.map((row, i) => (
                <span key={`st-${i}`}>
                  {tile({
                    value: formatNumber(num(row.total)),
                    label: String(row.label ?? '').toLowerCase(),
                    tone: 'grey',
                  })}
                </span>
              ))}
              {tile({
                value: formatNumber(num(problems.covered)),
                label: 'incidents covered',
                note: 'linked to a problem',
                tone: 'green',
              })}
              {tile({
                value: formatNumber(candidates.length),
                label: 'patterns not opened',
                note: 'items with repeat incidents',
                tone: candidates.length > 0 ? 'yellow' : 'green',
                link: 'problems',
              })}
            </div>
            <div className="itil-detail">
              <Mini
                title="Open problems"
                empty='<i class="fa fa-check-circle"></i> no open problems'
                wide
                foot="incidents each"
                rows={problemTop.map((row) => ({
                  label: String(row.title ?? ''),
                  href: hrefOf(`problems/view/${row.ID ?? ''}`),
                  total: formatNumber(num(row.incident_count)),
                }))}
              />
              <Mini
                title="Patterns with no problem opened yet"
                empty='<i class="fa fa-check-circle"></i> no item with repeat incidents'
                wide
                foot="incidents in the counting window"
                rows={candidates.map((row) => ({
                  label: String(row.ci_name ?? ''),
                  href: hrefOf(`cmdb/view/${row.ci_id ?? ''}`),
                  total: formatNumber(num(row.incidents)),
                }))}
              />
            </div>
          </>
        )}
      </div>

      <Section
        id="change-request"
        tone="green-mode"
        icon="fa-calendar-check"
        title="Change Request"
        note="what was asked for and approved - maintenance windows and change tickets"
        link="maintenance"
        linkLabel="Maintenance windows"
      />
      <div className="adm-card itil-card">
        {!requests.ready ? (
          <div className="adm-empty-inline">
            <i className="fas fa-database"></i>
            Maintenance windows are not in the database yet.
          </div>
        ) : (
          <>
            <MapNote entry={map['3']} what="Changes" />
            <div className="itil-tiles">
              {tile({
                value: formatNumber(num(requests.upcoming)),
                label: 'windows in 7 days',
                note: 'what is due to change',
                tone: 'green',
                link: 'maintenance',
              })}
              {tile({
                value: formatNumber(num(requests.open_now)),
                label: 'open right now',
                note: 'being worked on right now',
                tone: 'blue-hoki',
              })}
              {tile({
                value: formatNumber(num(requests.without_ticket)),
                label: 'with no ticket',
                note: 'a window nothing explains',
                tone: num(requests.without_ticket) > 0 ? 'yellow' : 'green',
              })}
              {tile({
                value: formatNumber(num(requests.conflicts)),
                label: 'suprapuneri',
                note: 'windows over the same items',
                tone: num(requests.conflicts) > 0 ? 'red' : 'green',
              })}
              {rfc.ready
                ? tile({
                    value: formatNumber(num(rfc.open)),
                    label: 'RFCs open',
                    note: 'change tickets',
                    tone: 'grey',
                    link: 'easeedesk/tickets',
                  })
                : null}
              {rfc.ready
                ? tile({
                    value: formatNumber(num(rfc.with_window)),
                    label: 'RFCs with a window',
                    note: 'cerute SI programate',
                    tone: 'green',
                  })
                : null}
            </div>
            <div className="itil-detail">
              <div className="itil-detail-col itil-detail-wide">
                <h5>Coming up in 7 days</h5>
                {next.length === 0 ? (
                  <p className="adm-muted itil-small">no window scheduled</p>
                ) : (
                  <table className="itil-mini">
                    <tbody>
                      {next.map((window, i) => (
                        <tr key={i}>
                          <td>
                            <a href={hrefOf('maintenance') ?? '/maintenance'}>{String(window.name ?? '')}</a>
                            <span className="adm-muted"> &middot; {String(window.company_name ?? '')}</span>
                          </td>
                          <td className="adm-nowrap itil-small">{phpDayTime(String(window.starts_at ?? ''))}</td>
                          <td className="adm-nowrap itil-small">
                            {!window.ticket_id ? (
                              <span className="adm-pill adm-pill-warn">no ticket</span>
                            ) : (
                              <a href={hrefOf(`easeedesk/ticket/${window.ticket_id}`) ?? '#'}>#{Number(window.ticket_id)}</a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="itil-detail-col itil-detail-wide">
                <h5>Reconcilierea cu ce s-a intamplat</h5>
                {!changes.ready ? (
                  <p className="adm-muted itil-small">without the change register this cannot be counted</p>
                ) : (
                  <>
                    <table className="itil-mini">
                      <tbody>
                        <tr>
                          <td>changes that fell inside a window</td>
                          <td className="itil-num itil-good">{formatNumber(num(changes.by_window))}</td>
                        </tr>
                        <tr>
                          <td>authorised after the fact, with a ticket</td>
                          <td className="itil-num">{formatNumber(num(changes.by_ticket))}</td>
                        </tr>
                        <tr>
                          <td>left with no explanation at all</td>
                          <td className="itil-num itil-bad">{formatNumber(num(changes.unauthorized))}</td>
                        </tr>
                      </tbody>
                    </table>
                    <span className="adm-muted itil-small">in the last {days} days</span>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Content>
  )
}
